import { useState } from 'react';
import type { PublisherFilterState } from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  filters: PublisherFilterState;
  onChange: (patch: Partial<PublisherFilterState>) => void;
}

export default function PublisherAdvancedFilters({ filters, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasAdvanced =
    filters.createdAfter || filters.createdBefore ||
    filters.bookCountMin !== undefined || filters.bookCountMax !== undefined ||
    filters.includeDeleted;

  return (
    <div className="mb-3 border border-neutral-200 rounded">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 flex items-center justify-between"
        aria-expanded={open}
      >
        <span>{t('App.Admin.Publishers.PublisherAdvancedFilters.Title')} {hasAdvanced && <span className="text-primary-600">•</span>}</span>
        <span className="text-xs text-neutral-500">{open ? t('App.Admin.Publishers.PublisherAdvancedFilters.Hide') : t('App.Admin.Publishers.PublisherAdvancedFilters.Show')}</span>
      </button>

      {open && (
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-t border-neutral-200">
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Publishers.PublisherAdvancedFilters.CreatedAfter')}
            <input
              type="date"
              value={filters.createdAfter?.slice(0, 10) ?? ''}
              onChange={(e) => onChange({
                createdAfter: e.target.value ? `${e.target.value}T00:00:00Z` : undefined,
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Publishers.PublisherAdvancedFilters.CreatedBefore')}
            <input
              type="date"
              value={filters.createdBefore?.slice(0, 10) ?? ''}
              onChange={(e) => onChange({
                createdBefore: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Publishers.PublisherAdvancedFilters.BookCountMin')}
            <input
              type="number"
              min={0}
              value={filters.bookCountMin ?? ''}
              onChange={(e) => onChange({
                bookCountMin: e.target.value === '' ? undefined : Number(e.target.value),
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Publishers.PublisherAdvancedFilters.BookCountMax')}
            <input
              type="number"
              min={0}
              value={filters.bookCountMax ?? ''}
              onChange={(e) => onChange({
                bookCountMax: e.target.value === '' ? undefined : Number(e.target.value),
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          <label className="flex items-center gap-2 text-sm text-neutral-700 mt-5">
            <input
              type="checkbox"
              checked={filters.includeDeleted ?? false}
              onChange={(e) => onChange({ includeDeleted: e.target.checked, page: 1 })}
            />
            {t('App.Admin.Publishers.PublisherAdvancedFilters.IncludeDeleted')}
          </label>
        </div>
      )}
    </div>
  );
}
